export const LIVE_VOICE_MINIMUM_SECONDS = 15;
export const LIVE_VOICE_USD_PER_MINUTE = 0.05;

export type LiveVoiceResponseUsage = {
  id: string;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
};

export type LiveVoiceUsageReport = {
  eventId: string;
  seconds?: number;
  final?: boolean;
  response?: LiveVoiceResponseUsage;
};

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function tokenCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
    ? value
    : null;
}

/** Duration events are cumulative. Hosted Responses usage is per response. */
export function buildLiveVoiceUsageReport(
  event: unknown
): LiveVoiceUsageReport | null {
  const envelope = record(event);
  if (!envelope || typeof envelope.event_id !== 'string') return null;
  if (
    envelope.type === 'session.usage.updated' ||
    envelope.type === 'session.closed'
  ) {
    const usage = record(envelope.usage);
    const seconds = usage?.seconds;
    if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0)
      return null;
    return {
      eventId: envelope.event_id,
      seconds,
      ...(envelope.type === 'session.closed' ? { final: true } : {}),
    };
  }
  if (envelope.type !== 'response.event') return null;
  const inner = record(envelope.event);
  if (!inner) return null;
  if (
    !['response.completed', 'response.failed', 'response.incomplete'].includes(
      String(inner?.type)
    )
  )
    return null;
  const response = record(inner.response);
  const usage = record(response?.usage);
  const inputTokens = tokenCount(usage?.input_tokens);
  const outputTokens = tokenCount(usage?.output_tokens);
  const cachedInputTokens = tokenCount(
    record(usage?.input_tokens_details)?.cached_tokens ?? 0
  );
  if (
    typeof response?.id !== 'string' ||
    !response.id ||
    inputTokens === null ||
    outputTokens === null ||
    cachedInputTokens === null ||
    cachedInputTokens > inputTokens
  )
    return null;
  return {
    eventId: envelope.event_id,
    response: { id: response.id, inputTokens, outputTokens, cachedInputTokens },
  };
}

export function calculateLiveVoiceDurationDelta(args: {
  reportedSeconds: number;
  billedSeconds: number;
}): { billedSeconds: number; deltaSeconds: number; ctcUsd: number } {
  if (
    !Number.isFinite(args.reportedSeconds) ||
    args.reportedSeconds < 0 ||
    !Number.isFinite(args.billedSeconds) ||
    args.billedSeconds < 0
  )
    throw new Error('Invalid Live duration');
  const billedSeconds = Math.max(
    LIVE_VOICE_MINIMUM_SECONDS,
    args.reportedSeconds,
    args.billedSeconds
  );
  const deltaSeconds = billedSeconds - args.billedSeconds;
  return {
    billedSeconds,
    deltaSeconds,
    ctcUsd: (deltaSeconds * LIVE_VOICE_USD_PER_MINUTE) / 60,
  };
}

export function calculateLiveVoiceResponseCost(args: {
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  inputUsdPerMillion: number;
  outputUsdPerMillion: number;
  cachedInputUsdPerMillion: number;
}): number {
  return (
    ((args.inputTokens - args.cachedInputTokens) * args.inputUsdPerMillion +
      args.cachedInputTokens * args.cachedInputUsdPerMillion +
      args.outputTokens * args.outputUsdPerMillion) /
    1_000_000
  );
}
